<template>
  <section class="faq-section">
    <div class="faq-wrapper">

      <!-- Section Header -->
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">
            Frequently Asked <span class="gradient-text">Questions</span>
          </h2>
          <p class="section-subtitle">
            Everything you need to know about QSpiders Eduversity programs
          </p>
        </div>
      </Motion>

      <!-- FAQ Accordion -->
      <div class="faq-list">
        <Motion
          v-for="(faq, i) in faqs"
          :key="i"
          :initial="{ opacity: 0, x: -30 }"
          :whileInView="{ opacity: 1, x: 0 }"
          :inViewOptions="{ once: true, margin: '-60px' }"
          :transition="{ duration: 0.5, delay: i * 0.06, ease: 'easeOut' }"
        >
          <div class="faq-item" :class="{ 'is-open': openIndex === i }" @mouseenter="openIndex = i">
            <button class="faq-question" @click="openIndex = i">
              <span class="faq-number">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="faq-text">{{ faq.question }}</span>
              <span class="faq-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            <div class="faq-answer-wrap">
              <div class="faq-answer">
                <p v-if="!Array.isArray(faq.answer)" class="faq-answer-text">{{ faq.answer }}</p>
                <ul v-else class="faq-answer-list">
                  <li v-for="(item, j) in faq.answer" :key="j" class="faq-answer-item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </Motion>
      </div>

      <!-- CTA Section -->
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-60px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="faq-cta">
          <div class="faq-cta-content">
          <h3 class="faq-cta-title">Ready to Transform Your Future?</h3>
          <p class="faq-cta-subtitle">Join 4.87L+ successful students and kickstart your journey to top tech companies</p>
          <button class="faq-cta-btn">
            Get Started Today
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
        </div>
      </Motion>

    </div>
  </section>
</template>

<script setup lang="ts">
const openIndex = ref<number>(0)

const faqs = [
  {
    question: 'What makes QSpiders Eduversity different from other programs?',
    answer: 'QSpiders Eduversity offers hands-on, experiential learning with real-world projects, industry mentorship, and direct placement support from 5200+ companies. Our programs combine academic excellence with practical tech exposure, backed by group companies like Fireflink, TechElevate, and TestYantra.'
  },
  {
    question: 'What specializations are available in the integrated programs?',
    answer: 'We offer integrated programs in B.Tech, M.Tech, BCA, MCA, and MBA with specializations in Full Stack Development & Testing with AI & ML, Fintech, and Data Analytics, in collaboration with India\'s top universities. Our curriculum is designed alongside industry leaders to align closely with real-world and market-driven requirements.'
  },
  {
    question: 'What kinds of projects will I work on as a student?',
    answer: 'Students work on real-life industry projects from Semester 1 itself. This includes building e-commerce platforms, developing your own brand identity, executing industry projects, and participating in 8+ national-level hackathons. These projects encourage collaboration, problem-solving, and creative thinking.'
  },
  {
    question: 'Who teaches the certification programs and regular university courses?',
    answer: [
      'Our classes are delivered by Software Engineers and Architects with 3–15 years of hands-on industry experience.',
      'All trainers are technology-certified and continuously update their skills with emerging technologies.',
      'We have 1,250+ trainers maintaining an excellent 4.3/5 average support rating.',
      'Academic classes are conducted by qualified university lecturers following the standard curriculum.',
      'You get industry-ready skills from practicing professionals + academic rigor from university faculty — the perfect combination for career success.'
    ]
  },
  {
    question: 'What kind of job opportunities can I expect after completing the program?',
    answer: 'With access to 5200+ companies across India including top brands like Amazon, TCS, Infosys, Capgemini, Deloitte, and many more, our students receive comprehensive placement support. We have placed 4.87 lakh+ students overall, with dedicated placement cell and frequent recruitment drives.'
  },
  {
    question: 'What is the internship structure in the program?',
    answer: 'Our programs include 6-month to 1-year internship opportunities in a corporate environment. This includes a 2+ month summer internship and a 6+ month industry internship, giving you hands-on experience in real-world scenarios.'
  },
  {
    question: 'What additional certifications are included in the program?',
    answer: 'Students receive free additional Online/Offline Certification Programs worth ₹1.5 Lakh, running parallel with the Master\'s program. This includes Hackathons and various industry-recognized certifications.'
  },
  {
    question: 'Where are QSpiders Eduversity centers located?',
    answer: [
      'We have centers across 16+ cities in India: Bengaluru, Hyderabad, Chennai, Pune, Mumbai, Noida, Mysore, Gurugram, Bhubaneswar, Kolkata, Tirupati, Ahmedabad, Delhi, Kochi, Coimbatore, Davanagere.',
      'All centers feature state-of-the-art infrastructure and cutting-edge facilities.'
    ]
  },
  {
    question: 'Is there support for aspiring entrepreneurs?',
    answer: 'Yes! Students with entrepreneurial abilities get access to investors, mentorship from successful entrepreneurs, and support in building and launching their own startups. We also conduct C-Suite Leadership Conferences and Global Entrepreneur Webinars.'
  },
  {
    question: 'How can I apply for the program?',
    answer: 'You can apply by clicking the Apply Now button or contacting us directly. Our counselors will guide you through the admission process, eligibility criteria, and help you choose the right program based on your career goals.'
  }
]
</script>

<style scoped>
/* ── Section ─────────────────────────────────────────────────────────────── */
.faq-section {
  padding: 6rem 2rem;
  position: relative;
  z-index: 10;
}

.faq-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.75;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── FAQ List ──────────────────────────────────────────────────────────────── */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 4rem;
}

/* ── FAQ Item ─────────────────────────────────────────────────────────────── */
.faq-item {
  border-radius: 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.faq-item:hover {
  border-color: rgba(255, 120, 0, 0.25);
}

.faq-item.is-open {
  border-color: rgba(255, 120, 0, 0.35);
  box-shadow: 0 8px 28px rgba(255, 120, 0, 0.06);
}

/* ── Question ─────────────────────────────────────────────────────────────── */
.faq-question {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.25s ease;
}

.faq-question:hover {
  background: rgba(255, 255, 255, 0.03);
}

.faq-number {
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--accent-orange);
  opacity: 0.6;
  flex-shrink: 0;
  min-width: 28px;
}

.faq-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  line-height: 1.4;
}

.faq-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s ease;
}

.faq-item.is-open .faq-arrow {
  transform: rotate(180deg);
  color: var(--accent-orange);
}

/* ── Answer ─────────────────────────────────────────────────────────────────── */
.faq-answer-wrap {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-item.is-open .faq-answer-wrap {
  max-height: 500px;
}

.faq-answer {
  padding: 0 1.5rem 1.5rem 3.25rem;
}

.faq-answer-text {
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.75;
  margin: 0;
}

.faq-answer-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.faq-answer-item {
  position: relative;
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.75;
  padding-left: 1.2rem;
}

.faq-answer-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55rem;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent-orange);
  opacity: 0.6;
}

/* ── CTA ────────────────────────────────────────────────────────────────────── */
.faq-cta {
  border-radius: 20px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  padding: 3rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.faq-cta::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff7800, #ffbb3f);
}

.faq-cta-content {
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.faq-cta-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.02em;
}

.faq-cta-subtitle {
  font-size: 1.05rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0;
  max-width: 480px;
}

.faq-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.faq-cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(255, 120, 0, 0.3);
}

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .faq-section {
    padding: 5rem 1rem;
  }

  .faq-question {
    padding: 1rem 1.25rem;
    gap: 0.75rem;
  }

  .faq-text {
    font-size: 1rem;
  }

  .faq-answer {
    padding: 0 1.25rem 1.25rem 2.75rem;
  }

  .faq-cta {
    padding: 2rem 1.25rem;
  }

  .faq-cta-title {
    font-size: 1.5rem;
  }

  .faq-cta-btn {
    width: 100%;
    justify-content: center;
  }

  .section-title {
    font-size: clamp(1.85rem, 7vw, 2.6rem);
  }
}
</style>
